/**
 * Schedule block timing: the defaults a new block gets, the cascade that keeps
 * later blocks consistent, and how a block reads on the calendar-style timeline —
 * its title, its time range, and where its own calendar day begins and ends.
 *
 * An event's schedule is a chain: each block starts when the one before it ends.
 * Start times are what gets stored — durations drive the chain but the resulting
 * times are saved explicitly, so the event-day page and the reminder job read a
 * plain startTime and need no knowledge of any of this.
 */

export interface ScheduleBlockValue {
    label?: string;
    type?: string;
    startTime?: string | Date;
    durationMinutes?: number;
    description?: string;
}

/** What a block lasts when nobody has said otherwise. */
export const DEFAULT_BLOCK_MINUTES = 120;

/** When a day starts, absent any other signal — the event carries a date, not a time. */
const DEFAULT_START_HOUR = 9;

const MINUTE_MS = 60_000;

const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toDate = (value: string | Date | undefined | null): Date | null => {
    if (!value) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
};

/**
 * How long a block lasts, for every reader that needs a number rather than "maybe
 * a number".
 *
 * A block loaded from the server can genuinely lack `durationMinutes`, and every
 * reader has to assume the same length for it. When they disagree the schedule
 * contradicts itself: the cascade reads such a block as two hours while a caller
 * defaulting to the 5-minute drag minimum draws it as a sliver, and one pixel of
 * resize then re-anchors it at 5 minutes and drags the rest of the day nearly two
 * hours earlier. This is the single place that default is decided.
 */
export const blockMinutes = (block: ScheduleBlockValue | undefined | null): number => block?.durationMinutes ?? DEFAULT_BLOCK_MINUTES;

const endOf = (block: ScheduleBlockValue): Date | null => {
    const start = toDate(block?.startTime);
    if (!start) {
        return null;
    }
    return new Date(start.getTime() + blockMinutes(block) * MINUTE_MS);
};

/**
 * Start time and duration for a block appended to the end of the schedule.
 *
 * The first block opens at 09:00 on the event's start date — the event stores a
 * date with no time, so there is nothing more specific to inherit. Every later
 * block begins when the previous one ends.
 */
export function nextScheduleBlockDefaults(
    blocks: ScheduleBlockValue[] | undefined,
    eventStartDate: string | Date | undefined
): { startTime: string; durationMinutes: number } {
    const previous = (blocks ?? [])[(blocks ?? []).length - 1];
    const previousEnd = previous ? endOf(previous) : null;

    if (previousEnd) {
        return { startTime: previousEnd.toISOString(), durationMinutes: DEFAULT_BLOCK_MINUTES };
    }

    // No usable previous block: open the day on the event's own date, or today if
    // the organiser has not set one yet.
    const base = toDate(eventStartDate) ?? new Date();
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), DEFAULT_START_HOUR, 0, 0, 0);

    return { startTime: start.toISOString(), durationMinutes: DEFAULT_BLOCK_MINUTES };
}

/**
 * Re-times every block after `changedIndex` so each starts when its predecessor
 * ends. The changed block and everything before it are left exactly as they are —
 * editing a start time or a duration pushes the rest of the day, rather than
 * leaving a schedule that disagrees with itself.
 *
 * The cascade never crosses a calendar-day boundary: once a block's own ORIGINAL
 * day (its day before this call touches anything) differs from its predecessor's
 * ORIGINAL day, that one pair is skipped rather than re-timed. Multi-day schedules
 * anchor each day independently (see generateSchedule) - an edit on an earlier day
 * must not silently relocate a later day's blocks, which is what a day-blind
 * cascade would otherwise do. The loop still keeps walking past the boundary
 * rather than stopping there, so a change reached from flat index 0 (moveBlock
 * re-anchors every move from index 0 via shiftScheduleTo, regardless of which day
 * the move happened on) still re-cascades a later day's own blocks from that
 * day's preserved anchor, instead of leaving the whole rest of the schedule
 * untouched. A freshly-generated day's blocks all start with no startTime yet
 * (see generateSchedule + shiftScheduleTo), so this guard is a no-op there and
 * generation-time cascading is unaffected.
 *
 * The comparison deliberately uses both blocks' ORIGINAL start times, not the
 * newly-computed `end` of the predecessor: a single day's last block can
 * legitimately run past midnight (e.g. a 23:00 block that runs 2 hours), and the
 * following same-day block must still absorb that overrun. What distinguishes a
 * "different day" from "same day that spills past midnight" is where the two
 * blocks started out, not where the cascade's arithmetic has ended up.
 */
export function cascadeScheduleStarts(blocks: ScheduleBlockValue[], changedIndex: number): ScheduleBlockValue[] {
    const original = blocks ?? [];
    const result = [...original];

    for (let i = Math.max(changedIndex, 0); i < result.length - 1; i++) {
        const end = endOf(result[i]);
        if (!end) {
            continue;
        }

        const originalStart = toDate(original[i]?.startTime);
        const originalNextStart = toDate(original[i + 1]?.startTime);
        if (originalStart && originalNextStart && !isSameCalendarDay(originalStart, originalNextStart)) {
            continue;
        }

        result[i + 1] = { ...result[i + 1], startTime: end.toISOString() };
    }

    return result;
}

const isSameCalendarDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const pad = (n: number) => `${n}`.padStart(2, '0');
/** "09:05" — exported so anything rendering a time (e.g. the schedule timeline's summary) reads it the same way as the block label does. */
export const timeOf = (date: Date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;
// Exported: schedule-timeline.component.ts's per-day column headings reuse this
// rather than formatting the date a second way.
export const dateOf = (date: Date) => `${SHORT_DAYS[date.getDay()]} ${date.getDate()} ${SHORT_MONTHS[date.getMonth()]}`;

/** "09:00–11:00" for a block's own row on the timeline, separately from its title. */
export function blockTimeRange(block: ScheduleBlockValue): string {
    const start = toDate(block?.startTime);
    if (!start) {
        return '';
    }
    const end = new Date(start.getTime() + blockMinutes(block) * MINUTE_MS);
    return `${timeOf(start)}–${timeOf(end)}`;
}

/**
 * The heading line of a block on the timeline: the block's own label and nothing
 * else.
 *
 * This replaced a compound "Round 1 · Sat 28 Mar · 09:00–11:00 (2h)" string that
 * was written for a row list the timeline no longer has. On a calendar block —
 * which already carries its day in the column heading above it and its own
 * blockTimeRange() on the line beneath it — that string printed the date and the
 * times a second time each, and then ellipsised, so a block read
 * "Round 1 · Sat 3 Oct · 09:30–11:30 (2…" over "09:30–11:30".
 */
export function scheduleBlockTitle(block: ScheduleBlockValue): string {
    return block?.label?.trim() || '(New Block)';
}

/**
 * The round number a block's label claims, or undefined if it claims none.
 *
 * Nothing in the data model links a schedule block to an EventRound, so the number is read
 * out of the organiser's free-text label - "Round 2" is round 2. A known simplification, and
 * the API's round-reminder.service.ts extracts it the same way; if this changes, change that
 * too, or the reminder job and the UI will disagree about which round is which.
 */
export function roundNumberOfBlock(block: ScheduleBlockValue | undefined | null): number | undefined {
    const match = block?.label?.match(/(\d+)/);
    return match ? Number(match[1]) : undefined;
}

/**
 * The schedule block describing a given round, so a caller holding a round number can reach
 * what the organiser wrote about it.
 *
 * Only blocks of type 'round' are considered: "Break 2" carries a 2 and is not round 2. Takes
 * the first match, so a schedule with two "Round 1" blocks resolves the same way every time
 * rather than depending on array order.
 */
export function scheduleBlockForRound<T extends ScheduleBlockValue>(
    blocks: T[] | undefined | null,
    roundNumber: number | undefined | null
): T | undefined {
    if (roundNumber === undefined || roundNumber === null) {
        return undefined;
    }
    return (blocks || []).find((b) => b?.type === 'round' && roundNumberOfBlock(b) === roundNumber);
}

/**
 * Whether the block at `index` opens (or closes) the run of blocks that share its
 * calendar day. The timeline renders one column per day and moves a block by
 * swapping it with its neighbour in the flat array, so a move across one of these
 * boundaries has no visible effect at all: cascadeScheduleStarts refuses to
 * re-time across a day boundary, so the block keeps its own day's start time and
 * simply stays where it was. The buttons are disabled on these blocks rather than
 * left to do nothing.
 *
 * Like the cascade, this compares where the two blocks *start*, so a day's last
 * block running past midnight still counts as part of that day. A block with no
 * start time cannot be placed on a day at all, and is deliberately not reported as
 * a boundary — that leaves the buttons enabled and the behaviour unchanged.
 */
export function isFirstBlockOfDay(blocks: ScheduleBlockValue[], index: number): boolean {
    const list = blocks ?? [];
    if (!list[index]) {
        return false;
    }
    if (index === 0) {
        return true;
    }
    return startsNewDay(list[index - 1], list[index]);
}

export function isLastBlockOfDay(blocks: ScheduleBlockValue[], index: number): boolean {
    const list = blocks ?? [];
    if (!list[index]) {
        return false;
    }
    if (index === list.length - 1) {
        return true;
    }
    return startsNewDay(list[index], list[index + 1]);
}

const startsNewDay = (before: ScheduleBlockValue, after: ScheduleBlockValue): boolean => {
    const beforeStart = toDate(before?.startTime);
    const afterStart = toDate(after?.startTime);
    if (!beforeStart || !afterStart) {
        return false;
    }
    return !isSameCalendarDay(beforeStart, afterStart);
};
